import { Injectable } from '@angular/core';
import { User } from '@supabase/supabase-js';
import { UserProfile } from '@app-libs/model';
import { UserProfileService } from './user-profile.service';
import { parseFullName, needsOAuthNameSync, hasOAuthNameData } from '../../utils/name-parser.utils';

@Injectable({
  providedIn: 'root',
})
export class OAuthProfileSyncService {
  constructor(private userProfileService: UserProfileService) {}

  /**
   * Synchronize user profile with OAuth data if needed
   *
   * Rules:
   * - If both first_name AND last_name are already set → Do NOT update
   * - If either first_name OR last_name is missing → Update BOTH from OAuth
   *
   * @param user - The authenticated Supabase user with OAuth metadata
   * @returns Promise<boolean> - true if profile was updated, false otherwise
   */
  async syncProfileFromOAuth(user: User): Promise<boolean> {
    try {
      // Check if user has OAuth name data
      if (!hasOAuthNameData(user.user_metadata)) {
        console.debug('[OAuthProfileSync] No OAuth name data found for user:', user.id);
        return false;
      }

      // Load current user profile
      const currentProfile = await this.userProfileService.loadUserProfile(user.id);
      if (!currentProfile) {
        console.warn('[OAuthProfileSync] No profile found for user:', user.id);
        return false;
      }

      // Check if profile needs OAuth sync
      if (!needsOAuthNameSync(currentProfile)) {
        console.debug(
          '[OAuthProfileSync] Profile already has name data, skipping sync for user:',
          user.id,
        );
        return false;
      }

      // Parse OAuth name data
      const { firstName, lastName } = parseFullName(user.user_metadata['name']);

      // Prepare profile update data
      const updateData: Partial<UserProfile> = {};

      // Only update if profile needs sync AND we have OAuth name data
      // If either name field is missing, update BOTH fields from OAuth data
      // If both name fields are already set, don't update anything
      if (needsOAuthNameSync(currentProfile)) {
        if (firstName) {
          updateData.first_name = firstName;
        }
        if (lastName) {
          updateData.last_name = lastName;
        }
      }

      // If no updates needed, return false
      if (Object.keys(updateData).length === 0) {
        console.debug('[OAuthProfileSync] No profile updates needed for user:', user.id);
        return false;
      }

      // Update profile with OAuth data
      const updatedProfile = await this.userProfileService.updateUserProfile(user.id, updateData);

      if (updatedProfile) {
        console.info(
          '[OAuthProfileSync] Successfully updated profile from OAuth data for user:',
          user.id,
          updateData,
        );
        return true;
      } else {
        console.warn(
          '[OAuthProfileSync] Failed to update profile from OAuth data for user:',
          user.id,
        );
        return false;
      }
    } catch (error) {
      console.error('[OAuthProfileSync] Error syncing profile from OAuth:', error);
      return false;
    }
  }

  /**
   * Check if a user profile should be synchronized with OAuth data
   *
   * @param user - The authenticated Supabase user
   * @param profile - The current user profile
   * @returns boolean - true if sync should be performed
   */
  shouldSyncProfile(user: User, profile: UserProfile): boolean {
    return hasOAuthNameData(user.user_metadata) && needsOAuthNameSync(profile);
  }

  /**
   * Check if a field is considered empty (null, undefined, or empty string)
   *
   * @param field - The field value to check
   * @returns boolean - true if field is empty, false otherwise
   */
  private isFieldEmpty(field: string | null | undefined): boolean {
    return field === null || field === undefined || field.trim() === '';
  }
}
